package org.maplibre.compose.expressions.dsl

import kotlin.jvm.JvmName
import org.maplibre.compose.expressions.ast.Expression
import org.maplibre.compose.expressions.ast.Semiliteral
import org.maplibre.compose.expressions.value.BooleanValue
import org.maplibre.compose.expressions.value.ExpressionValue
import org.maplibre.compose.expressions.value.IntValue
import org.maplibre.compose.expressions.value.ListValue
import org.maplibre.compose.expressions.value.MapValue
import org.maplibre.compose.expressions.value.StringValue

/**
 * Builds a list by evaluating each element. Unlike [const], elements may read feature data or
 * contain other expressions. Nested literal arrays remain literal; use another [list] for a nested
 * array of expressions.
 *
 * Copies [elements] so later changes to the list do not change the expression.
 */
public fun <T : ExpressionValue?> list(elements: List<Expression<T>>): Expression<ListValue<T>> =
  Semiliteral(elements.toList())

/** Builds a list by evaluating each element. See the list overload of [list]. */
public fun <T : ExpressionValue?> list(vararg elements: Expression<T>): Expression<ListValue<T>> =
  list(elements.asList())

/** Returns the item at [index]. */
@JvmName("getAt")
public operator fun <T : ExpressionValue?> Expression<ListValue<T>>.get(
  index: Expression<IntValue>,
): Expression<T> = call("at", index, this)

/** Returns the item at [index]. */
@JvmName("getAt")
public operator fun <T : ExpressionValue?> Expression<ListValue<T>>.get(index: Int): Expression<T> =
  get(const(index))

/** Returns whether this list contains the [item]. */
@JvmName("containsList")
public fun <T : ExpressionValue?> Expression<ListValue<T>>.contains(
  item: Expression<T>,
): Expression<BooleanValue> = call("in", item, this)

/**
 * Returns the first index at which the [item] is located in this list, or `-1` if it cannot be
 * found.
 *
 * @param startIndex An optional index from where to begin the search.
 */
@JvmName("indexOfList")
public fun <T : ExpressionValue?> Expression<ListValue<T>>.indexOf(
  item: Expression<T>,
  startIndex: Expression<IntValue>? = null,
): Expression<IntValue> = call("index-of", listOfNotNull(item, this, startIndex))

/**
 * Returns the first index at which the [item] is located in this list, or `-1` if it cannot be
 * found.
 *
 * @param startIndex An optional index from where to begin the search.
 */
@JvmName("indexOfList")
public fun <T : ExpressionValue?> Expression<ListValue<T>>.indexOf(
  item: Expression<T>,
  startIndex: Int? = null,
): Expression<IntValue> = indexOf(item, startIndex?.let { const(it) })

/**
 * Returns the items in this list from the [startIndex] (inclusive) to the end of this list if
 * [endIndex] is not specified or `null`, otherwise to [endIndex] (exclusive).
 */
public fun <T : ExpressionValue?> Expression<ListValue<T>>.slice(
  startIndex: Expression<IntValue>,
  endIndex: Expression<IntValue>? = null,
): Expression<ListValue<T>> = call("slice", listOfNotNull(this, startIndex, endIndex))

/**
 * Returns the items in this list from the [startIndex] (inclusive) to the end of this list if
 * [endIndex] is not specified or `null`, otherwise to [endIndex] (exclusive).
 */
public fun <T : ExpressionValue?> Expression<ListValue<T>>.slice(
  startIndex: Int,
  endIndex: Int? = null,
): Expression<ListValue<T>> = slice(const(startIndex), endIndex?.let { const(it) })

/** Gets the length of a this list. */
@JvmName("lengthOfList")
public fun Expression<ListValue<*>>.length(): Expression<IntValue> = call("length", this)

/**
 * Returns a string formed by concatenating the items in this list, inserting [separator] between
 * each item.
 */
public fun Expression<ListValue<StringValue>>.join(
  separator: Expression<StringValue>,
): Expression<StringValue> = call("join", this, separator)

/**
 * Returns a string formed by concatenating the items in this list, inserting [separator] between
 * each item.
 */
public fun Expression<ListValue<StringValue>>.join(separator: String): Expression<StringValue> =
  join(const(separator))

/** Returns the value corresponding the given [key] or `null` if it is not present in this map. */
public operator fun <T : ExpressionValue?> Expression<MapValue<T>>.get(
  key: Expression<StringValue>,
): Expression<T?> = call("get", key, this)

/** Returns the value corresponding the given [key] or `null` if it is not present in this map. */
public operator fun <T : ExpressionValue?> Expression<MapValue<T>>.get(
  key: String,
): Expression<T?> = get(const(key))

/** Returns whether the given [key] is in this map. */
public fun Expression<MapValue<*>>.has(key: Expression<StringValue>): Expression<BooleanValue> =
  call("has", key, this)

/** Returns whether the given [key] is in this map. */
public fun Expression<MapValue<*>>.has(key: String): Expression<BooleanValue> = has(const(key))
